import React, { useEffect, useState } from 'react';
import { Edit, Plus, Shield, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { policiesApi, employeesApi } from '../lib/api';

const DEFAULT_POLICY = {
  screenshot_enabled: true,
  screenshot_interval_min: 5,
  recording_enabled: false,
  recording_mode: 'on_alert',
  usb_policy: 'allow',
  block_installs: false,
  website_block_categories: ['malware', 'phishing'],
};

function detectType(policyJson = {}) {
  if (policyJson.usb_policy && policyJson.usb_policy !== 'allow') return 'usb';
  if (policyJson.screenshot_enabled) return 'screenshot';
  return 'web_blocking';
}

export default function Policies() {
  const [policies, setPolicies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const [form, setForm] = useState({
    name: '',
    type: 'screenshot',
    json: JSON.stringify(DEFAULT_POLICY, null, 2),
  });

  const loadPolicies = async () => {
    setLoading(true);
    setError('');
    try {
      const [policiesResponse, employeesResponse] = await Promise.all([
        policiesApi.list(),
        employeesApi.list({ page: 1, page_size: 200 }),
      ]);
      setPolicies(policiesResponse.data?.items || []);
      setEmployees(employeesResponse.data?.items || []);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to load policies.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicies();
  }, []);

  const openCreate = () => {
    setSelectedPolicy(null);
    setForm({
      name: '',
      type: 'screenshot',
      json: JSON.stringify(DEFAULT_POLICY, null, 2),
    });
    setShowEditor(true);
  };

  const openEdit = (policy) => {
    setSelectedPolicy(policy);
    setForm({
      name: policy.name,
      type: detectType(policy.json),
      json: JSON.stringify(policy.json || DEFAULT_POLICY, null, 2),
    });
    setShowEditor(true);
  };

  const savePolicy = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const parsed = JSON.parse(form.json);
      if (selectedPolicy) {
        await policiesApi.update(selectedPolicy.id, { name: form.name, json: parsed });
        toast.success('Policy updated.');
      } else {
        await policiesApi.create({ name: form.name, json: parsed });
        toast.success('Policy created.');
      }
      setShowEditor(false);
      loadPolicies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save policy JSON.');
    } finally {
      setSaving(false);
    }
  };

  const assignPolicy = async (event) => {
    event.preventDefault();
    if (!selectedPolicy || !assignEmployeeId) return;
    try {
      await policiesApi.assign({
        policy_id: selectedPolicy.id,
        scope_type: 'employee',
        scope_id: assignEmployeeId,
      });
      toast.success('Policy assigned.');
      setShowAssign(false);
      setAssignEmployeeId('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign policy.');
    }
  };

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 p-6 text-slate-300" data-testid="policies-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Policies</h1>
          <p className="mt-2 text-slate-500">
            Versioned monitoring and enforcement policies for screenshots, web controls, and USB behavior.
          </p>
        </div>
        <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          Create Policy
        </Button>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-300">
          {error}
        </div>
      ) : null}

      {loading ? (
        <LoadingSkeleton type="card" />
      ) : policies.length ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {policies.map((policy) => (
            <Card key={policy.id} className="border-slate-700 bg-slate-800 shadow-lg">
              <CardHeader className="pb-0">
                <CardTitle className="flex items-center justify-between gap-3 text-white">
                  <span className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-blue-400" />
                    {policy.name}
                  </span>
                  <span className="text-xs font-medium text-slate-500">v{policy.version}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="flex flex-wrap gap-2">
                  <span className="rounded-full bg-slate-900 px-2 py-1 text-xs text-slate-300">
                    {detectType(policy.json)}
                  </span>
                  <span className="rounded-full bg-slate-900 px-2 py-1 text-xs text-slate-300">
                    Assigned count: —
                  </span>
                  <span className="rounded-full bg-slate-900 px-2 py-1 text-xs text-slate-300">
                    Status: active
                  </span>
                </div>
                <p className="text-sm text-slate-500">
                  Created by {policy.creator_email || 'Unknown'} • {policy.json?.website_block_categories?.length || 0}{' '}
                  category rules
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-slate-600 bg-slate-900 text-slate-300 hover:bg-slate-700"
                    onClick={() => openEdit(policy)}
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-slate-600 bg-slate-900 text-slate-300 hover:bg-slate-700"
                    onClick={() => {
                      setSelectedPolicy(policy);
                      setShowAssign(true);
                    }}
                  >
                    <UserPlus className="h-4 w-4" />
                    Assign
                  </Button>
                  <Button
                    size="sm"
                    className="bg-red-600 text-white hover:bg-red-700"
                    onClick={() => toast.error('Delete is not available in the current backend.')}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<Shield className="h-8 w-8" />}
          title="No policies"
          description="Create a monitoring policy to start configuring controls."
          action={
            <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Create Policy
            </Button>
          }
        />
      )}

      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="border-slate-700 bg-slate-800 text-slate-300">
          <DialogHeader>
            <DialogTitle className="text-white">{selectedPolicy ? 'Edit Policy' : 'Create Policy'}</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={savePolicy}>
            <input
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              placeholder="Policy name"
              className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <select
              value={form.type}
              onChange={(event) => setForm((current) => ({ ...current, type: event.target.value }))}
              className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-white outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="web_blocking">web_blocking</option>
              <option value="usb">usb</option>
              <option value="screenshot">screenshot</option>
            </select>
            <textarea
              value={form.json}
              onChange={(event) => setForm((current) => ({ ...current, json: event.target.value }))}
              className="min-h-[260px] w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 font-mono text-sm text-white outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Button className="w-full bg-blue-600 text-white hover:bg-blue-700" disabled={saving} type="submit">
              {saving ? 'Saving...' : 'Save Policy'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="border-slate-700 bg-slate-800 text-slate-300">
          <DialogHeader>
            <DialogTitle className="text-white">Assign Policy</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={assignPolicy}>
            <select
              value={assignEmployeeId}
              onChange={(event) => setAssignEmployeeId(event.target.value)}
              className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-white outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select employee</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </select>
            <Button className="w-full bg-blue-600 text-white hover:bg-blue-700" type="submit">
              Assign
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
